import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { Newspaper, Bot } from "lucide-react";

const CATEGORIES = [
  { key: "", label: "All" },
  { key: "platform", label: "Platform" },
  { key: "election_news", label: "Election News" },
  { key: "candidate_update", label: "Candidate Updates" },
  { key: "accountability", label: "Accountability" },
];

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    setPosts(null);
    const params = { limit: 30 };
    if (category) params.category = category;
    api.get("/public/news", { params }).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, [category]);

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 mb-4">
          <Newspaper className="h-3 w-3 mr-1" /> Witness Your Vote Newsroom
        </Badge>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="news-title">News</h1>
        <p className="text-muted-foreground mt-3">
          Platform updates, Ontario municipal election news and accountability stories — factual, sourced and
          non-partisan. System-collected items are always reviewed by an administrator before publishing.
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="News categories" data-testid="news-category-filters">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            role="tab"
            aria-selected={category === c.key}
            onClick={() => setSearchParams(c.key ? { category: c.key } : {})}
            data-testid={`news-filter-${c.key || "all"}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${
              category === c.key
                ? "bg-primary text-white border-primary"
                : "bg-white text-slate-700 border-slate-300 hover:bg-secondary"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-grid">
        {!posts && [1, 2, 3].map((i) => <Skeleton key={i} className="h-72" />)}
        {posts?.map((post) => (
          <Link key={post.id} to={`/news/${post.slug}`} className="group block" data-testid={`news-card-${post.slug}`}>
            <Card className="h-full overflow-hidden hover:shadow-md transition-shadow duration-150">
              {post.image && <img src={post.image} alt="" className="h-44 w-full object-cover" loading="lazy" />}
              <CardContent className="p-5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-accent mb-2">
                  {post.published_at ? new Date(post.published_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : ""}
                  {post.featured && <span className="ml-2 text-amber-600">Featured</span>}
                </p>
                <h2 className="font-heading font-semibold text-base text-foreground leading-snug group-hover:text-primary">{post.title}</h2>
                <p className="text-xs text-muted-foreground leading-relaxed mt-2 line-clamp-4">{post.summary}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {post.system_generated && (
                    <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-600 border-slate-300">
                      <Bot className="h-3 w-3 mr-1" /> System-collected · admin reviewed
                    </Badge>
                  )}
                  {(post.municipality_tags || []).slice(0, 2).map((t) => (
                    <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
        {posts && posts.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="news-empty">No stories in this category yet.</p>
        )}
      </div>
    </div>
  );
}
